import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../redux/store";
import useTabTitle from "../hooks/useTabTitle.hook";
import { getPermissions } from "../redux/slices/permissionsSlice";
import { PermissionsItem } from "../interfaces/permissions-interface";
import {
  createUserRole,
  getUserRoleNames,
} from "../redux/slices/userRoleSlice";
import { CreateUserRolePayload } from "../interfaces/user-role.interface";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import LoadingOverlay from "../components/LoadingOverlay";

const CreateUserRolePage: React.FC = () => {
  useTabTitle();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useDetectSession();

  const {
    permissions,
    status: permissionsStatus,
    error: permissionsError,
  } = useSelector((state: RootState) => state.permissions);
  const { userRoleNames } = useSelector((state: RootState) => state.userRole);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [status, setStatus] = useState<string>("loading");
  const [error, setError] = useState<string | null>(null);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const [roleName, setRoleName] = useState<string>("");
  const [selectedPermissions, setSelectedPermissions] = useState<{
    [key: string]: PermissionsItem;
  }>({});
  const [createRolePayload, setCreateRolePayload] =
    useState<CreateUserRolePayload>({
      roleName: "",
      rolePermissions: [],
      createdBy: "",
    });
  const [showRoleNameErr, setShowRoleNameErr] = useState<boolean>(false);
  const [createRoleErr, setCreateRoleErr] = useState<string>("");

  useEffect(() => {
    dispatch(getPermissions())
      .unwrap()
      .then((permissions) => {
        if (permissions?.length !== 0) {
          const permissionsByKey: any = {};
          permissions?.forEach(
            (item: PermissionsItem) =>
              (permissionsByKey[item.permissionKey] = {
                ...item,
                permissions: { view: false, edit: false, delete: false },
              })
          );
          setSelectedPermissions(permissionsByKey);
        }
      });
    dispatch(getUserRoleNames());
  }, [dispatch]);

  useEffect(() => {
    const matchedNames =
      userRoleNames?.filter((item: string) => item === roleName) ?? [];
    if (matchedNames.length !== 0) {
      setCreateRoleErr("Role Name already exists in database.");
    } else {
      setCreateRoleErr("");
    }
  }, [roleName, userRoleNames]);

  useEffect(() => {
    if (permissionsError) {
      setError(permissionsError);
    }
    if (permissionsStatus) {
      setStatus(permissionsStatus);
    }
  }, [permissionsError, permissionsStatus]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const buildCreateUserRolePayload = (permissionItem: PermissionsItem) => {
    const isPreviouslyIncluded = createRolePayload.rolePermissions.find(
      (item: PermissionsItem) =>
        item.permissionKey === permissionItem.permissionKey
    );
    if (!isPreviouslyIncluded) {
      setCreateRolePayload({
        roleName: roleName,
        rolePermissions: [...createRolePayload.rolePermissions, permissionItem],
        createdBy: currentUser?.fullname ?? "",
      });
    } else {
      setCreateRolePayload({
        roleName: roleName,
        rolePermissions: createRolePayload.rolePermissions.map(
          (item: PermissionsItem) => {
            if (item.permissionKey === permissionItem.permissionKey) {
              return permissionItem;
            }
            return item;
          }
        ),
        createdBy: currentUser?.fullname ?? "",
      });
    }
  };

  if (status === "failed") {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <IconButton onClick={handleBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={pageHeading}>
          Create User Role
        </Typography>
      </Box>

      <LoadingOverlay loading={status === "loading"} />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Role Name */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6" width={130}>
          Role Name
        </Typography>
        <TextField
          label="Enter Role Name"
          value={roleName}
          onChange={(e) => setRoleName(e.target?.value)}
          fullWidth
          margin="dense"
          sx={inputStyle()}
        />
      </Box>

      {/* Permissions Options */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6" textAlign={"center"} margin={2}>
          Role Permissions
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            overflowX: "initial",
            "& th": {
              backgroundColor: "#E1E1E1",
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Module Name</TableCell>
                <TableCell>View</TableCell>
                <TableCell>Edit</TableCell>
                <TableCell>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions?.map((item: PermissionsItem) => (
                <TableRow key={item?.moduleName}>
                  <TableCell>{item?.moduleName}</TableCell>
                  <TableCell>
                    {item?.permissions?.view && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.view
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            ...selectedPermissions[item.permissionKey],
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.view,
                              edit: selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildCreateUserRolePayload(modifiedItem);
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.permissions?.edit && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.edit
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            ...selectedPermissions[item.permissionKey],
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.edit
                                ? true
                                : selectedPermissions[item.permissionKey]
                                    ?.permissions?.view,
                              edit: !selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildCreateUserRolePayload(modifiedItem);
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {item?.permissions?.delete && (
                      <Checkbox
                        checked={
                          selectedPermissions[item.permissionKey]?.permissions
                            ?.delete
                        }
                        onClick={(_e) => {
                          const modifiedItem = {
                            ...selectedPermissions[item.permissionKey],
                            permissions: {
                              view: !selectedPermissions[item.permissionKey]
                                ?.permissions?.delete
                                ? true
                                : selectedPermissions[item.permissionKey]
                                    ?.permissions?.view,
                              edit: selectedPermissions[item.permissionKey]
                                ?.permissions?.edit,
                              delete:
                                !selectedPermissions[item.permissionKey]
                                  ?.permissions?.delete,
                            },
                          };
                          setSelectedPermissions({
                            ...selectedPermissions,
                            [item.permissionKey]: modifiedItem,
                          });
                          buildCreateUserRolePayload(modifiedItem);
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        display="flex"
        margin={2}
        flexDirection={"row"}
        alignItems={"flex-end"}
        justifyContent={"flex-end"}
      >
        <Button
          variant="contained"
          disabled={
            !roleName ||
            createRolePayload.rolePermissions.length === 0 ||
            createRolePayload.rolePermissions.every(
              (item: PermissionsItem) =>
                !item.permissions.view &&
                !item.permissions.edit &&
                !item.permissions.delete
            )
          }
          onClick={() => {
            if (createRoleErr) {
              setShowRoleNameErr(true);
              return;
            }

            setCreateRoleErr("");

            dispatch(createUserRole({ data: createRolePayload }))
              .then(() => {
                setSnackbarMessage("Successfully created user role");
                setSnackbarSeverity("success");
                setOpenSnackbar(true);
                setRoleName("");

                const permissionsByKey: any = {};
                permissions?.forEach(
                  (item: PermissionsItem) =>
                    (permissionsByKey[item.permissionKey] = {
                      ...item,
                      permissions: {
                        view: false,
                        edit: false,
                        delete: false,
                      },
                    })
                );
                setSelectedPermissions(permissionsByKey);

                setCreateRolePayload({
                  roleName: "",
                  rolePermissions: [],
                  createdBy: "",
                });

                dispatch(getUserRoleNames());
                navigate("/manage-user-role");
              })
              .catch(() => {
                setCreateRoleErr("Failed to create user role");
                setShowRoleNameErr(true);
              });
          }}
          sx={primaryBtnStyle}
        >
          Save
        </Button>
        <Button
          variant="outlined"
          style={{ marginLeft: 15 }}
          onClick={() => {
            setRoleName("");
            setCreateRolePayload({
              roleName: "",
              rolePermissions: [],
              createdBy: "",
            });
            navigate("/manage-user-role");
          }}
          sx={primaryBtnStyleOutline}
        >
          Cancel
        </Button>
      </Box>

      <Dialog
        open={showRoleNameErr}
        onClose={() => {
          setShowRoleNameErr(false);
        }}
      >
        <DialogTitle id="alert-dialog-title">
          Failed to Create User Role
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {createRoleErr}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRoleNameErr(false);
            }}
            color="primary"
            sx={[primaryBtnStyleOutline, { width: "60px" }]}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </AppContainer>
  );
};

export default CreateUserRolePage;
